import React, {useCallback, useRef, useEffect, useState} from 'react';
import * as THREE from 'three';
import {useAppDispatch, useAppSelector} from 'store/customer';
import TWEEN from '@tweenjs/tween.js';
import {USE_LEGACY_TWEEN_GROUP} from 'components/customer/Preview3DCommon/lib/tween';
import {PreviewPosition} from 'components/customer/Preview3D/types';
import {OrbitControls} from 'three/examples/jsm/controls/OrbitControls';
import {debounce} from 'lodash';
import {
    setIsPreviewHighlighted,
    setExpandedViewCameraPosition,
    getExpandedViewCameraPosition,
} from 'components/customer/Preview3DCommon/store/viewerSlice';
import {shallowEqual} from 'react-redux';
import {
    computeOrthographicFrustum,
    tweenCameraPosition,
    tweenCameraZoom,
    computeFitFromBounds,
} from 'components/customer/Preview3DCommon/lib/cameraUtils';

export {computeFitFromBounds};

// Keeps a fit requested before the scene has geometry a pure no-op, so it
// can't settle an in-flight viewpoint tween.
const sceneHasVisibleMesh = (scene: THREE.Scene | undefined): boolean => {
    if (!scene) return false;

    let found = false;
    scene.traverse((obj) => {
        if (found) return;
        if (obj instanceof THREE.Mesh && obj.visible) found = true;
    });

    return found;
};

export type CameraFit = {zoom: number; panNdcX: number; panNdcY: number};

// World translation for a post-projection NDC pan. Must be applied to the
// camera *and* its orbit target, never the target alone.
export const computeFitPanDelta = (
    camera: THREE.OrthographicCamera,
    target: THREE.Vector3,
    fit: CameraFit
): THREE.Vector3 => {
    const viewDir = new THREE.Vector3()
        .subVectors(target, camera.position)
        .normalize();
    const screenRight = new THREE.Vector3()
        .crossVectors(viewDir, camera.up)
        .normalize();
    const screenUp = new THREE.Vector3()
        .crossVectors(screenRight, viewDir)
        .normalize();

    const worldPerNdcX = (camera.right - camera.left) / 2 / fit.zoom;
    const worldPerNdcY = (camera.top - camera.bottom) / 2 / fit.zoom;

    return new THREE.Vector3()
        .addScaledVector(screenRight, -fit.panNdcX * worldPerNdcX)
        .addScaledVector(screenUp, -fit.panNdcY * worldPerNdcY);
};

export const computeFit = (
    scene: THREE.Scene | undefined,
    camera: THREE.OrthographicCamera
): CameraFit | null => {
    if (!scene) return null;

    const originalZoom = camera.zoom;
    camera.zoom = 1;
    scene.updateMatrixWorld(true);
    camera.updateMatrixWorld(true);
    camera.updateProjectionMatrix();

    let xMin = Infinity;
    let xMax = -Infinity;
    let yMin = Infinity;
    let yMax = -Infinity;
    let hasMesh = false;

    const v = new THREE.Vector3();
    scene.traverse((obj) => {
        if (!(obj instanceof THREE.Mesh) || !obj.visible) return;
        // precise=true: bypass cached geometry.boundingBox so resized
        // cabinets don't report stale bounds after a dimension change.
        const box = new THREE.Box3().setFromObject(obj, true);
        if (box.isEmpty()) return;
        hasMesh = true;
        for (let i = 0; i < 8; i++) {
            v.set(
                i & 1 ? box.max.x : box.min.x,
                i & 2 ? box.max.y : box.min.y,
                i & 4 ? box.max.z : box.min.z
            ).project(camera);
            if (v.x < xMin) xMin = v.x;
            if (v.x > xMax) xMax = v.x;
            if (v.y < yMin) yMin = v.y;
            if (v.y > yMax) yMax = v.y;
        }
    });

    camera.zoom = originalZoom;
    camera.updateProjectionMatrix();

    if (!hasMesh) return null;
    return computeFitFromBounds(xMin, xMax, yMin, yMax);
};

const usePreview3DCameraSetup = (
    previewRef: React.MutableRefObject<HTMLDivElement>,
    renderer: React.MutableRefObject<THREE.WebGLRenderer>,
    scene: React.MutableRefObject<THREE.Scene>,
    expanded?: boolean
) => {
    const dispatch = useAppDispatch();
    const camera = useRef<THREE.OrthographicCamera>();
    const controls = useRef<OrbitControls>();
    const expandedViewInitialLoad = useRef(false);
    const expandedViewCameraPosition = useAppSelector(
        getExpandedViewCameraPosition,
        shallowEqual
    );

    const [firstCameraChange, setFirstCameraChange] = useState(false);

    const setupCamera = useCallback(() => {
        if (previewRef.current) {
            let dimension = previewRef.current.getBoundingClientRect();

            if (dimension?.x == 0) {
                dimension = new DOMRect(202, 492, 43, 360);
            }

            const frustum = computeOrthographicFrustum(
                dimension.width,
                dimension.height
            );

            if (camera.current !== undefined) {
                camera.current.left = frustum.left;
                camera.current.right = frustum.right;
                camera.current.top = frustum.top;
                camera.current.bottom = frustum.bottom;
            } else {
                camera.current = new THREE.OrthographicCamera(
                    frustum.left,
                    frustum.right,
                    frustum.top,
                    frustum.bottom,
                    1,
                    20000
                );
            }

            camera.current.updateProjectionMatrix();

            return camera;
        }
    }, []);

    const activeFitZoom = useRef<{
        tween: {stop: () => void};
        zoomEnd: number;
    } | null>(null);
    const activeFitPan = useRef<{
        tween: {stop: () => void};
        positionEnd: THREE.Vector3;
        targetEnd: THREE.Vector3;
    } | null>(null);
    // Resolvers for callers waiting on the fit to settle. Superseded fits are
    // resolved by whichever fit finishes last, so a caller can never hang.
    const fitSettleResolvers = useRef<(() => void)[]>([]);

    // Viewpoint tweens from setupCameraPosition/setupZoomAndControls. The fit
    // snaps these to their end values first so it never measures a camera that
    // is still sweeping between viewpoints.
    const activeViewTweens = useRef<{stop: () => void}[]>([]);
    const pendingViewEnd = useRef<{
        position: THREE.Vector3 | null;
        target: THREE.Vector3 | null;
        zoom: number | null;
    }>({position: null, target: null, zoom: null});

    const settlePendingView = useCallback(() => {
        if (!activeViewTweens.current.length) return;

        activeViewTweens.current.forEach((tween) => tween.stop());
        activeViewTweens.current = [];

        const end = pendingViewEnd.current;
        pendingViewEnd.current = {position: null, target: null, zoom: null};

        if (!camera.current) return;

        if (end.position) camera.current.position.copy(end.position);
        if (end.target && controls.current)
            controls.current.target.copy(end.target);
        if (end.zoom !== null) camera.current.zoom = end.zoom;

        if (controls.current) camera.current.lookAt(controls.current.target);
        camera.current.updateProjectionMatrix();
    }, []);

    const resolveFitSettled = useCallback(() => {
        const resolvers = fitSettleResolvers.current;
        fitSettleResolvers.current = [];
        resolvers.forEach((resolve) => resolve());
    }, []);

    const fitCameraToScene = useCallback((): Promise<void> => {
        if (!camera.current) {
            resolveFitSettled();
            return Promise.resolve();
        }

        if (activeFitZoom.current) {
            const {tween, zoomEnd} = activeFitZoom.current;
            camera.current.zoom = zoomEnd;
            camera.current.updateProjectionMatrix();
            tween.stop();
            activeFitZoom.current = null;
        }
        if (activeFitPan.current) {
            const {tween, positionEnd, targetEnd} = activeFitPan.current;
            camera.current.position.copy(positionEnd);
            if (controls.current) controls.current.target.copy(targetEnd);
            tween.stop();
            activeFitPan.current = null;
        }

        // Nothing to frame yet — leave any viewpoint tween running.
        if (!sceneHasVisibleMesh(scene.current)) {
            resolveFitSettled();
            return Promise.resolve();
        }

        settlePendingView();

        const fit = computeFit(scene.current, camera.current);
        if (!fit) {
            resolveFitSettled();
            return Promise.resolve();
        }

        const settled = new Promise<void>((resolve) => {
            fitSettleResolvers.current.push(resolve);
        });

        const zoomTween = new TWEEN.Tween(
            {zoom: camera.current.zoom},
            USE_LEGACY_TWEEN_GROUP
        )
            .to({zoom: fit.zoom}, 500)
            .easing(TWEEN.Easing.Cubic.Out)
            .onUpdate(({zoom}) => {
                if (!camera.current) return;
                camera.current.zoom = zoom;
                camera.current.updateProjectionMatrix();
            })
            .onComplete(() => {
                if (activeFitZoom.current?.tween === zoomTween) {
                    activeFitZoom.current = null;
                }
                if (!activeFitPan.current) resolveFitSettled();
            });
        zoomTween.start();
        activeFitZoom.current = {tween: zoomTween, zoomEnd: fit.zoom};

        if (controls.current && (fit.panNdcX !== 0 || fit.panNdcY !== 0)) {
            const cam = camera.current;
            const delta = computeFitPanDelta(cam, controls.current.target, fit);

            const positionStart = cam.position.clone();
            const targetStart = controls.current.target.clone();
            const positionEnd = positionStart.clone().add(delta);
            const targetEnd = targetStart.clone().add(delta);

            // Camera and target must move by the same delta. Moving only the
            // target is not a pan: OrbitControls.update() recomputes position
            // as target + (position - target), leaving the position put and
            // only re-aiming — the iso view rotates instead of translating.
            const progress = {t: 0};
            const panTween = new TWEEN.Tween(progress, USE_LEGACY_TWEEN_GROUP)
                .to({t: 1}, 500)
                .easing(TWEEN.Easing.Cubic.Out)
                .onUpdate(({t}) => {
                    if (!camera.current || !controls.current) return;
                    camera.current.position
                        .copy(positionStart)
                        .addScaledVector(delta, t);
                    controls.current.target
                        .copy(targetStart)
                        .addScaledVector(delta, t);
                })
                .onComplete(() => {
                    if (activeFitPan.current?.tween === panTween) {
                        activeFitPan.current = null;
                    }
                    if (!activeFitZoom.current) resolveFitSettled();
                });
            panTween.start();
            activeFitPan.current = {
                tween: panTween,
                positionEnd,
                targetEnd,
            };
        }

        return settled;
    }, []);

    const setupZoomAndControls = () => {
        if (camera.current) {
            activeViewTweens.current.push(tweenCameraZoom(camera.current, 1));
            pendingViewEnd.current.zoom = 1;
        }

        if (controls.current) {
            activeViewTweens.current.push(
                new TWEEN.Tween(controls.current.target, USE_LEGACY_TWEEN_GROUP)
                    .to({x: 0, y: 0, z: 0}, 500)
                    .easing(TWEEN.Easing.Cubic.Out)
                    .start()
            );
            pendingViewEnd.current.target = new THREE.Vector3(0, 0, 0);
        }
    };

    const setupCameraPosition = (position: PreviewPosition) => {
        const cam = camera.current;
        if (!cam) return;

        const cameraPosition = {x: 0, y: 0, z: 0} as {
            x: number;
            y: number;
            z: number;
        };

        switch (position) {
            case 'RIGHT':
                cameraPosition.x = 2000;
                cameraPosition.y = -3000;
                cameraPosition.z = 2000;
                break;
            case 'LEFT_LEVELED':
                cameraPosition.x = -4100;
                cameraPosition.y = 0;
                cameraPosition.z = controls.current
                    ? controls.current.target.z
                    : 0;
                break;
            case 'FRONT':
                cameraPosition.x = 1;
                cameraPosition.y = -4000;
                cameraPosition.z = controls.current
                    ? controls.current.target.z
                    : 0;
                break;
            case 'RIGHT_LEVELED':
                cameraPosition.x = 4100;
                cameraPosition.y = 0;
                cameraPosition.z = controls.current
                    ? controls.current.target.z
                    : 0;
                break;
            default:
                cameraPosition.x = -2000;
                cameraPosition.y = -3000;
                cameraPosition.z = 2000;
                break;
        }

        cam.up.set(0, 0, 1);

        activeViewTweens.current.push(tweenCameraPosition(cam, cameraPosition));
        pendingViewEnd.current.position = new THREE.Vector3(
            cameraPosition.x,
            cameraPosition.y,
            cameraPosition.z
        );

        setupZoomAndControls();

        if (expanded && !expandedViewInitialLoad.current) {
            expandedViewInitialLoad.current = true;

            activeViewTweens.current.push(
                tweenCameraPosition(cam, expandedViewCameraPosition),
                tweenCameraZoom(cam, expandedViewCameraPosition.zoom)
            );
            pendingViewEnd.current.position = new THREE.Vector3(
                expandedViewCameraPosition.x,
                expandedViewCameraPosition.y,
                expandedViewCameraPosition.z
            );
            pendingViewEnd.current.zoom = expandedViewCameraPosition.zoom;
        }
    };

    const setupOrbitControls = () => {
        controls.current = new OrbitControls(
            camera.current,
            renderer.current.domElement
        );

        controls.current.screenSpacePanning = true;
    };

    useEffect(() => {
        const handleControlsChange = debounce(() => {
            if (firstCameraChange) dispatch(setIsPreviewHighlighted(false));
            else setFirstCameraChange(true);
        }, 300);

        controls.current?.addEventListener('change', handleControlsChange);

        return () => {
            controls.current?.removeEventListener(
                'change',
                handleControlsChange
            );
        };
    }, [firstCameraChange]);

    useEffect(() => {
        const handleControlsChange = debounce(() => {
            if (!expanded)
                dispatch(
                    setExpandedViewCameraPosition({
                        x: camera.current?.position.x,
                        y: camera.current?.position.y,
                        z: camera.current?.position.z,
                        zoom: camera.current?.zoom,
                    })
                );
        }, 100);

        controls.current?.addEventListener('change', handleControlsChange);

        return () => {
            controls.current?.removeEventListener(
                'change',
                handleControlsChange
            );
        };
    }, [expanded]);

    return {
        camera,
        setupCamera,
        setupCameraPosition,
        controls,
        setupOrbitControls,
        fitCameraToScene,
    };
};

export default usePreview3DCameraSetup;
